import React, { useState, useMemo, useEffect } from 'react'
import { Select, Tooltip } from 'antd'
import { withNamespaces } from '@edulastic/localization'
import {
  EduButton,
  captureSentryException,
  notification,
} from '@edulastic/common'
import { segmentApi } from '@edulastic/api'
import { isEmpty } from 'lodash'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withRouter, Link } from 'react-router-dom'
import { IconEditMetaData, IconInfo } from '@edulastic/icons'

import {
  StyledApplyButton,
  StyledDivider,
  StyledEmptyStateContainer,
  StyledEmptyStateText,
  StyledErrorText,
  StyledGradientLoader,
  StyledLoadingContainer,
  StyledMetadataItem,
  StyledMetadataLabel,
  StyledMetadataLabelContainer,
  StyledMetadataSection,
  StyledRecommendationItem,
  StyledRecommendationLabel,
  StyledRecommendationList,
  StyledRecommendationListItem,
  StyledRecommendationsContainer,
  StyledRecommendationText,
  StyledSelect,
  StyledShowSuggestionsButton,
  StyledSpinnerWrapper,
  StyledStandardContainer,
  StyledStandardEditContainer,
  StyledStandardIdentifierContainer,
  StyledThinkingText,
  StyledStandardTooltipContainer,
  StyledStandardTooltipLabel,
  StyledStandardTooltipRow,
  StyledStandardSetDropdownContainer,
  StyledStandardSetSelect,
  StyledStandardSetLabel,
  StyledStandardIdentifier,
  StyledStandardDescription,
  StyledStandardLowConfidenceWarning,
  StyledStandardsAddedList,
  StyledStandardsAddedRow,
  StyledMoreStandards,
  StyledStandardTooltipWrapper,
  StyledAdditionalStandardsModal,
} from './styled'
import { StyledRequired } from '../../Reports/subPages/dataWarehouseReports/GoalsAndInterventions/common/components/Form/styled-components'
import { getCurrentQuestionSelector } from '../../sharedDucks/questions'
import { DIFFICULTY_OPTIONS, DOK_OPTIONS } from '../constants/form'
import {
  getAIQuestionAssistanceState,
  generateAIQuestionMetadataAction,
  updateUserUpdatedDataAction,
  resetAIQuestionMetadataStateAction,
} from '../ducks/questionAssistance'
import {
  getStandardCurriculumData,
  getStandardIdsToLog,
  getStandardSetOptions,
  getStandardTooltipItems,
  hasQuestionContentForMetadata,
  getConfidenceScoreForStandard,
} from '../utils/AIQuestionAssistance'
import {
  getCurriculumsListSelector,
  standardsSelector,
} from '../../src/selectors/dictionaries'
import { getItemIdSelector } from '../../sharedDucks/itemDetail'

import {
  isAIQuestionAssistanceRecommendationsEnabledSelector,
  getAllInterestedCurriculumsSelector,
  getInterestedSubjectsSelector,
  getInterestedGradesSelector,
} from '../../src/selectors/user'
import StandardsModal from '../../../assessment/containers/QuestionMetadata/StandardsModal'
import { getDictStandardsForCurriculumAction } from '../../src/actions/dictionaries'

const segmentConfig = { attachUserData: true }
const LOW_CONFIDENCE_THRESHOLD = 0.7

const QuestionMetadataSection = ({
  t: i18translate,
  aiQuestionAssistanceState,
  currentQuestion,
  generateAIQuestionMetadata,
  onSaveSuggestedMetadata,
  allCurriculums,
  itemId = null,
  match,
  updateUserUpdatedData,
  isAIQuestionAssistanceRecommendationsEnabled,
  resetAIQuestionMetadataState,
  interestedCurriculums,
  interestedSubjects,
  interestedGrades,
  getCurriculumStandards,
  curriculumStandardsLoading,
}) => {
  const testId = match?.params?.testId || null
  const { apiStatus, result, errorMessage } = aiQuestionAssistanceState || {}

  const [dokEdited, setDokEdited] = useState(false)
  const [difficultyEdited, setDifficultyEdited] = useState(false)
  const [standardsEdited, setStandardsEdited] = useState(false)
  const [selectedStandardSetId, setSelectedStandardSetId] = useState(null)
  const [
    showStandardsSelectionModal,
    setShowStandardsSelectionModal,
  ] = useState(false)
  const [showStandardsAddedModal, setShowStandardsAddedModal] = useState(false)

  const isLoading = apiStatus === 'INITIATED'
  const hasError = apiStatus === 'FAILED'

  const hasQuestionContent = hasQuestionContentForMetadata(currentQuestion)

  const { standardSetOptions } = useMemo(
    () => getStandardSetOptions({ interestedCurriculums, interestedSubjects }),
    [interestedCurriculums, interestedSubjects]
  )

  const flow = testId ? 'testAuthoring' : 'itemAuthoring'

  const suggestedMetaData = result?.suggestedMetaData
  const userUpdatedData = result?.userUpdatedData || {}
  const {
    dok: suggestedDok = '',
    difficulty: suggestedDifficulty = '',
    standards: suggestedStandards = [],
    suggested_edits: suggestedEdits = {},
    standardsMetadata: suggestedStandardsMetadata = [],
  } = suggestedMetaData || {}

  const {
    stimulus: suggestedStimulusEdit = '',
    options: suggestedOptionsEdits = [],
  } = suggestedEdits || {}

  const dok = userUpdatedData?.dok || suggestedDok || ''
  const difficulty = userUpdatedData?.difficulty || suggestedDifficulty || ''
  const hasStandardSetOptions = standardSetOptions?.length > 0

  useEffect(() => {
    if (hasStandardSetOptions) {
      try {
        segmentApi.genericEventTrack(
          'AIQuestionMetadata:StandardSetInputDisplayed',
          {
            flow,
            itemId,
            testId,
            qid: currentQuestion?.id,
            sessionId: window?.sessionStorage?.kid,
            questionType: currentQuestion?.type,
            questionTitle: currentQuestion?.title,
          },
          segmentConfig
        )
      } catch (error) {
        captureSentryException(error, {
          errorMessage:
            'AI Question Assistance - METADATA: Error tracking StandardSetInputDisplayed event',
          itemId,
          questionId: currentQuestion?.id,
          sessionId: window?.sessionStorage?.kid,
        })
      }
    }
  }, [hasStandardSetOptions])

  const handleDokChange = (value) => {
    updateUserUpdatedData({ dok: value })
    setDokEdited(true)
  }

  const handleDifficultyChange = (value) => {
    updateUserUpdatedData({ difficulty: value })
    setDifficultyEdited(true)
  }

  const handleGenerateClick = () => {
    const curriculumId = selectedStandardSetId || null

    const subject = curriculumId
      ? interestedCurriculums?.find(
          (curriculum) => curriculum?._id === curriculumId
        )?.subject
      : null

    if (currentQuestion) {
      generateAIQuestionMetadata({
        question: currentQuestion,
        testId,
        selectedStandardSetId: curriculumId,
        subject,
      })

      segmentApi.genericEventTrack(
        'AIQuestionMetadata:SuggestMetadataClick',
        {
          flow,
          itemId,
          testId,
          qid: currentQuestion?.id,
          sessionId: window?.sessionStorage?.kid,
          questionType: currentQuestion?.type,
          questionTitle: currentQuestion?.title,
        },
        segmentConfig
      )
    }
  }

  const difficultyOptions = DIFFICULTY_OPTIONS.filter(
    (option) => option.value !== ''
  )
  const dokOptions = DOK_OPTIONS.filter((option) => option.value !== '')

  const handleShowStandardsSelectionModal = (value) => {
    setShowStandardsSelectionModal(value)
  }

  const updateAlignment = (data = {}) => {
    const suggestedStandard = suggestedStandards[0]
    const selectedStandards = (data?.eloStandards || []).filter(
      (standard) => standard._id !== suggestedStandard._id
    )
    if (selectedStandards?.length > 0) {
      setStandardsEdited(true)
    } else if (
      data?.eloStandards?.length === 1 &&
      data.eloStandards[0]._id === suggestedStandard._id
    ) {
      setStandardsEdited(false)
    }
    updateUserUpdatedData({ standards: data?.eloStandards || [] })
    handleShowStandardsSelectionModal(false)
  }

  const renderRecommendations = () => {
    if (
      isEmpty(suggestedEdits) ||
      (isEmpty(suggestedStimulusEdit) && isEmpty(suggestedOptionsEdits))
    ) {
      return null
    }

    const hasStimulusEdit = suggestedStimulusEdit?.trim?.()?.length > 0
    const hasOptionsEdits =
      Array.isArray(suggestedOptionsEdits) && suggestedOptionsEdits.length > 0

    return (
      <>
        <StyledRecommendationsContainer data-cy="pearAssistRecommendations">
          <StyledMetadataLabelContainer>
            <StyledMetadataLabel>Recommendations</StyledMetadataLabel>
            <Tooltip title="Improve readability of your question">
              <IconInfo
                width="14px"
                height="14px"
                fill="#919191"
                aria-hidden="true"
              />
            </Tooltip>
          </StyledMetadataLabelContainer>

          {hasStimulusEdit && (
            <StyledRecommendationItem>
              <StyledRecommendationLabel>Stimulus:</StyledRecommendationLabel>
              <StyledRecommendationText data-cy="pearAssistRecommendationStimulus">
                {suggestedStimulusEdit}
              </StyledRecommendationText>
            </StyledRecommendationItem>
          )}

          {hasOptionsEdits && (
            <StyledRecommendationItem>
              <StyledRecommendationLabel>Options:</StyledRecommendationLabel>
              <StyledRecommendationList data-cy="pearAssistRecommendationOptions">
                {suggestedOptionsEdits.map((optionEdit, index) => (
                  <StyledRecommendationListItem key={index}>
                    {optionEdit}
                  </StyledRecommendationListItem>
                ))}
              </StyledRecommendationList>
            </StyledRecommendationItem>
          )}
        </StyledRecommendationsContainer>
        <StyledDivider />
      </>
    )
  }

  const renderStandards = () => {
    if (!suggestedStandards || suggestedStandards?.length === 0) {
      return null
    }

    const defaultGrades = interestedGrades?.length > 0 ? interestedGrades : []

    const userUpdatedStandards = userUpdatedData?.standards?.length
      ? userUpdatedData.standards
      : []
    const standards =
      standardsEdited && userUpdatedStandards?.length > 0
        ? userUpdatedStandards
        : [suggestedStandards[0]]
    const firstStandard = standards[0]
    const additionalStandards = standards.filter(
      (s) => s._id !== firstStandard._id
    )

    const firstStandardConfidence = getConfidenceScoreForStandard(
      firstStandard,
      suggestedStandardsMetadata
    )
    const showLowConfidenceWarning =
      !standardsEdited && firstStandardConfidence < LOW_CONFIDENCE_THRESHOLD

    const curriculum = getStandardCurriculumData(firstStandard, allCurriculums)
    const standardTooltipItems = getStandardTooltipItems(standards, curriculum)

    const {
      subject,
      _id: curriculumId,
      pdlCurriculumId,
      pdlAdoptYear,
      pdlCurriculumName = '',
      pdlSubjectLabel = '',
      pdlSubjectId,
      pdlGrades = [],
    } = curriculum || {}

    const pdlStandardProps = {
      pdlCurriculumId,
      pdlAdoptYear,
      pdlCurriculumName,
      pdlSubjectLabel,
      pdlSubjectId,
      pdlGrades,
      grades: defaultGrades,
      subject,
    }

    return (
      <StyledStandardContainer data-cy="pearAssistStandards">
        <StyledMetadataLabelContainer>
          <StyledMetadataLabel>Standards</StyledMetadataLabel>
          <StyledStandardTooltipWrapper>
            <Tooltip
              title={
                <StyledStandardTooltipContainer>
                  {standardTooltipItems.map((item) => (
                    <StyledStandardTooltipRow key={item.label}>
                      <StyledStandardTooltipLabel>
                        {item.label}:{' '}
                      </StyledStandardTooltipLabel>
                      {item.value}
                    </StyledStandardTooltipRow>
                  ))}
                </StyledStandardTooltipContainer>
              }
            >
              <IconInfo
                width="14px"
                height="14px"
                fill="#919191"
                aria-hidden="true"
              />
            </Tooltip>
          </StyledStandardTooltipWrapper>
        </StyledMetadataLabelContainer>

        <>
          <StyledStandardIdentifierContainer>
            <StyledStandardIdentifier data-cy="pearAssistStandardIdentifier">
              {firstStandard.identifier}
            </StyledStandardIdentifier>
            <StyledStandardEditContainer
              data-cy="pearAssistStandardEdit"
              onClick={() => handleShowStandardsSelectionModal(true)}
            >
              <IconEditMetaData aria-hidden="true" />
            </StyledStandardEditContainer>
          </StyledStandardIdentifierContainer>
          <StyledStandardDescription data-cy="pearAssistStandardDescription">
            {firstStandard.description}
          </StyledStandardDescription>
          {showLowConfidenceWarning && (
            <StyledStandardLowConfidenceWarning
              data-cy="pearAssistStandardLowConfidenceWarning"
              role="status"
            >
              Low confidence on standards suggested
            </StyledStandardLowConfidenceWarning>
          )}
          {additionalStandards?.length > 0 && (
            <StyledMoreStandards
              type="button"
              data-cy="pearAssistStandardsMoreLink"
              onClick={() => setShowStandardsAddedModal(true)}
            >
              and {additionalStandards.length} more standard(s)
            </StyledMoreStandards>
          )}
        </>

        <StyledAdditionalStandardsModal
          visible={showStandardsAddedModal}
          title="Standards Added"
          footer={null}
          centered
          onCancel={() => setShowStandardsAddedModal(false)}
          width={550}
          maskClosable
        >
          <StyledStandardsAddedList data-cy="pearAssistAdditionalStandardsList">
            {additionalStandards.map((s) => (
              <StyledStandardsAddedRow key={s._id}>
                <StyledStandardIdentifier
                  data-cy={`pearAssistAdditionalStandardsIdentifier-${s.identifier}`}
                >
                  {s.identifier}
                </StyledStandardIdentifier>
                <StyledStandardDescription
                  data-cy={`pearAssistAdditionalStandardsDescription-${s.identifier}`}
                >
                  {s.description}
                </StyledStandardDescription>
              </StyledStandardsAddedRow>
            ))}
          </StyledStandardsAddedList>
        </StyledAdditionalStandardsModal>

        {showStandardsSelectionModal && (
          <StandardsModal
            t={i18translate}
            defaultSubject={subject}
            defaultGrades={defaultGrades}
            defaultStandards={standards}
            defaultStandard={{
              curriculum,
              id: curriculumId,
            }}
            pdlStandardProps={pdlStandardProps}
            visible={showStandardsSelectionModal}
            curriculums={allCurriculums}
            onApply={updateAlignment}
            setSubject={() => {}}
            onCancel={() => handleShowStandardsSelectionModal(false)}
            getCurriculumStandards={getCurriculumStandards}
            curriculumStandardsLoading={curriculumStandardsLoading}
            showAllInterestedCurriculums
          />
        )}
      </StyledStandardContainer>
    )
  }

  const handleApplyChanges = () => {
    if (isEmpty(suggestedMetaData)) {
      captureSentryException(
        new Error('No suggested metadata available to apply'),
        {
          errorMessage:
            'AI Question Assistance - METADATA: No suggested metadata found when applying AI metadata',
          itemId,
          questionId: currentQuestion?.id,
        }
      )

      notification({
        type: 'error',
        msg: 'An error occurred. Please try again later.',
      })

      resetAIQuestionMetadataState()
      return
    }

    if (onSaveSuggestedMetadata) {
      onSaveSuggestedMetadata()

      segmentApi.genericEventTrack(
        'AIQuestionMetadata:SaveSuggestionsClick',
        {
          flow,
          itemId,
          testId,
          qid: currentQuestion?.id,
          sessionId: window?.sessionStorage?.kid,
          questionType: currentQuestion?.type,
          questionTitle: currentQuestion?.title,
          dok,
          difficulty,
          standardIds: getStandardIdsToLog({
            userUpdatedStandards: userUpdatedData?.standards || [],
            suggestedStandards,
            standardsEdited,
          }),
          dokEdited,
          difficultyEdited,
          standardsEdited,
        },
        segmentConfig
      )
    }
  }

  const renderMetadataContent = () => (
    <>
      <StyledMetadataSection data-cy="pearAssistMetadataSuccess">
        {isAIQuestionAssistanceRecommendationsEnabled &&
          renderRecommendations()}
        {renderStandards()}

        <StyledDivider />

        {dok && (
          <StyledMetadataItem>
            <StyledMetadataLabelContainer>
              <StyledMetadataLabel data-cy="pearAssistDokLabel">
                Depth of Knowledge
              </StyledMetadataLabel>
            </StyledMetadataLabelContainer>
            <StyledSelect
              data-cy="pearAssistDokSelect"
              value={dok}
              onChange={handleDokChange}
            >
              {dokOptions.map((option) => (
                <Select.Option key={option.label} value={option.label}>
                  {option.label}
                </Select.Option>
              ))}
            </StyledSelect>
          </StyledMetadataItem>
        )}

        <StyledDivider />

        {difficulty && (
          <StyledMetadataItem>
            <StyledMetadataLabelContainer>
              <StyledMetadataLabel>Difficulty Level</StyledMetadataLabel>
            </StyledMetadataLabelContainer>
            <StyledSelect
              data-cy="pearAssistDifficultySelect"
              value={difficulty}
              onChange={handleDifficultyChange}
            >
              {difficultyOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </StyledSelect>
          </StyledMetadataItem>
        )}
      </StyledMetadataSection>

      <StyledApplyButton>
        <EduButton
          data-cy="pearAssistSaveSuggestions"
          ml="0px"
          type="primary"
          onClick={handleApplyChanges}
          disabled={!currentQuestion}
        >
          Save Suggestions and Item
        </EduButton>
      </StyledApplyButton>
    </>
  )

  if (isLoading) {
    return (
      <StyledLoadingContainer data-cy="pearAssistMetadataLoading">
        <StyledSpinnerWrapper>
          <StyledGradientLoader />
        </StyledSpinnerWrapper>
        <StyledThinkingText data-cy="pearAssistMetadataLoadingText">
          Reading question to generate standards and other details
        </StyledThinkingText>
      </StyledLoadingContainer>
    )
  }

  if (apiStatus === 'SUCCESS' && !isEmpty(suggestedMetaData)) {
    return renderMetadataContent()
  }

  if (hasError) {
    return (
      <StyledErrorText type="danger" data-cy="pearAssistMetadataError">
        {errorMessage || 'Failed to generate metadata suggestions'}
      </StyledErrorText>
    )
  }

  const isGenerateDisabled = !hasQuestionContent || !selectedStandardSetId

  return (
    <StyledEmptyStateContainer data-cy="pearAssistMetadataEmpty">
      <StyledEmptyStateText data-cy="pearAssistMetadataEmptyText">
        After typing your question, click here to see AI suggested metadata
        (including standards).
      </StyledEmptyStateText>

      <StyledDivider />

      <StyledStandardSetDropdownContainer>
        <StyledStandardSetLabel>
          Standard Set<StyledRequired>*</StyledRequired>
        </StyledStandardSetLabel>
        {standardSetOptions?.length > 0 ? (
          <StyledStandardSetSelect
            data-cy="pearAssistStandardSetSelect"
            value={selectedStandardSetId ?? undefined}
            placeholder="Select"
            getPopupContainer={() => document.body}
            dropdownStyle={{ zIndex: 2000 }}
            onChange={(value) => setSelectedStandardSetId(value)}
          >
            {standardSetOptions.map((curriculum) => (
              <Select.Option key={curriculum._id} value={curriculum._id}>
                {curriculum.name || curriculum.curriculum}
              </Select.Option>
            ))}
          </StyledStandardSetSelect>
        ) : (
          <StyledEmptyStateText
            color="#262626"
            marginTop="0px"
            data-cy="pearAssistStandardSetEmptyText"
          >
            Please add standard set, grade, and subject in the{' '}
            <Link to="/author/profile">profile page</Link>.
          </StyledEmptyStateText>
        )}
      </StyledStandardSetDropdownContainer>

      <StyledShowSuggestionsButton>
        <EduButton
          data-cy="pearAssistSuggestMetadata"
          ml="0px"
          type="primary"
          onClick={handleGenerateClick}
          disabled={isGenerateDisabled}
        >
          Suggest Metadata
        </EduButton>
      </StyledShowSuggestionsButton>
    </StyledEmptyStateContainer>
  )
}

const enhance = compose(
  withNamespaces('assessment'),
  withRouter,
  connect(
    (state) => ({
      aiQuestionAssistanceState: getAIQuestionAssistanceState(state),
      currentQuestion: getCurrentQuestionSelector(state),
      allCurriculums: getCurriculumsListSelector(state),
      itemId: getItemIdSelector(state),
      isAIQuestionAssistanceRecommendationsEnabled: isAIQuestionAssistanceRecommendationsEnabledSelector(
        state
      ),
      interestedCurriculums: getAllInterestedCurriculumsSelector(state),
      interestedSubjects: getInterestedSubjectsSelector(state),
      interestedGrades: getInterestedGradesSelector(state),
      curriculumStandardsLoading: standardsSelector(state)?.loading,
    }),
    {
      generateAIQuestionMetadata: generateAIQuestionMetadataAction,
      updateUserUpdatedData: updateUserUpdatedDataAction,
      resetAIQuestionMetadataState: resetAIQuestionMetadataStateAction,
      getCurriculumStandards: getDictStandardsForCurriculumAction,
    }
  )
)
export default enhance(QuestionMetadataSection)
